import { AwsCredentialsBanner } from "@/components/shared/AwsCredentialsBanner"
import { BackendConnectionBanner } from "@/components/shared/BackendConnectionBanner"
import { ErrorBoundary } from "@/components/shared/ErrorBoundary"
import { LoadingPage } from "@/components/shared/LoadingSpinner"
import { ROUTES } from "@/routes"
import { useMantineColorScheme } from "@mantine/core"
import React, { Suspense, lazy, useEffect } from "react"
import { Navigate, Route, Routes } from "react-router-dom"

// Route-level code splitting: each page (and its dependencies, e.g. recharts, jspdf)
// is only fetched/compiled when its route is actually visited, instead of eagerly
// loading every page's dependency graph on initial app load.
// Layout/ProtectedRoute pull in the full @/store barrel (every feature store) plus
// PaymentSetupModal/WelcomeModal, so they're lazy too — public pages like the landing
// page never render them and shouldn't pay for that graph.
const Layout = lazy(() => import("@/components/shared/Layout"))
const ProtectedRoute = lazy(() => import("@/components/shared/ProtectedRoute"))
const AboutPage = lazy(() => import("@/components/navbar/about-page/AboutPage"))
const FeaturesPage = lazy(() => import("@/components/navbar/features-page/FeaturesPage"))
const SupportPage = lazy(() => import("@/components/navbar/support-page/SupportPage"))
const TiersPage = lazy(() => import("@/components/navbar/tiers-page/TiersPage"))
const AuthCallback = lazy(() => import("@/pages/auth-callback/AuthCallback"))
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password-page/ForgotPasswordPage"))
const LandingPage = lazy(() => import("@/pages/landing-page/LandingPage"))
const LoginPage = lazy(() => import("@/pages/login-page/LoginPage"))
const BillingAlertsPage = lazy(() => import("@/pages/main-page/billing/BillingAlertsPage"))
const CostAndUsagePage = lazy(() => import("@/pages/main-page/billing/CostAndUsagePage"))
const CostAdvisorPage = lazy(() => import("@/pages/main-page/cloud-advisor/CostAdvisorPage"))
const PerformanceAdvisorPage = lazy(() => import("@/pages/main-page/cloud-advisor/PerformanceAdvisorPage"))
const DepartmentsPage = lazy(() => import("@/pages/main-page/cloud-allocation/DepartmentsPage"))
const MembersPage = lazy(() => import("@/pages/main-page/cloud-allocation/MembersPage"))
const UnassignedPage = lazy(() => import("@/pages/main-page/cloud-allocation/UnassignedPage"))
const CostAlertPage = lazy(() => import("@/pages/main-page/cloud-manager/CostAlertPage"))
const ManageServerPage = lazy(() => import("@/pages/main-page/cloud-manager/ManageServerPage"))
const PerformanceAlertPage = lazy(() => import("@/pages/main-page/cloud-manager/PerformanceAlertPage"))
const ReportsPage = lazy(() => import("@/pages/main-page/cloud-manager/ReportsPage"))
const Dashboard = lazy(() => import("@/pages/main-page/dashboard/Dashboard"))
const MyAccountPage = lazy(() => import("@/pages/main-page/my-account-page/MyAccountPage"))
const NotificationsPage = lazy(() => import("@/pages/main-page/notifications/NotificationsPage"))
const SettingsPage = lazy(() => import("@/pages/main-page/settings-page/SettingsPage"))
const NotFoundPage = lazy(() => import("@/pages/not-found/NotFoundPage"))
const RegistrationPage = lazy(() => import("@/pages/registration-page/RegistrationPage"))
const DeptAdminDashboardPage = lazy(() => import("@/pages/dept-admin-page/dashboard/DeptAdminDashboardPage"))
const DeptAdminDepartmentPage = lazy(() => import("@/pages/dept-admin-page/department/DeptAdminDepartmentPage"))
const DeptAdminMembersPage = lazy(() => import("@/pages/dept-admin-page/members/DeptAdminMembersPage"))
const DeptAdminNotificationsPage = lazy(() => import("@/pages/dept-admin-page/notifications/DeptAdminNotificationsPage"))
const DeptAdminAccountPage = lazy(() => import("@/pages/dept-admin-page/account/DeptAdminAccountPage"))
const DeptAdminSettingsPage = lazy(() => import("@/pages/dept-admin-page/settings/DeptAdminSettingsPage"))
const DeptMemberDashboardPage = lazy(() => import("@/pages/dept-member-page/dashboard/DeptMemberDashboardPage"))
const DeptMemberDepartmentPage = lazy(() => import("@/pages/dept-member-page/department/DeptMemberDepartmentPage"))
const DeptMemberNotificationsPage = lazy(() => import("@/pages/dept-member-page/notifications/DeptMemberNotificationsPage"))
const DeptMemberAccountPage = lazy(() => import("@/pages/dept-member-page/account/DeptMemberAccountPage"))
const DeptMemberSettingsPage = lazy(() => import("@/pages/dept-member-page/settings/DeptMemberSettingsPage"))
const RoleAwareRedirect = lazy(() => import("@/components/shared/RoleAwareRedirect"))

// Wrapper to force light mode on public pages without changing user's theme preference
const LightModeOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colorScheme } = useMantineColorScheme()

  useEffect(() => {
    // Save the current theme preference
    const savedTheme = colorScheme

    // Force light mode classes on DOM
    document.documentElement.classList.add("light")
    document.documentElement.classList.remove("dark")
    document.documentElement.setAttribute("data-mantine-color-scheme", "light")

    // Cleanup: restore the user's theme preference when leaving public page
    return () => {
      if (savedTheme === "dark") {
        document.documentElement.classList.add("dark")
        document.documentElement.classList.remove("light")
        document.documentElement.setAttribute("data-mantine-color-scheme", "dark")
      }
    }
  }, [colorScheme])

  return <>{children}</>
}

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <BackendConnectionBanner />
        <AwsCredentialsBanner />
        <Suspense fallback={<LoadingPage />}>
        <Routes>
          {/* ==================== Public Routes ==================== */}
          <Route
            path={ROUTES.public.home.path}
            element={
              <LightModeOnly>
                <LandingPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.tiers.path}
            element={
              <LightModeOnly>
                <TiersPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.features.path}
            element={
              <LightModeOnly>
                <FeaturesPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.about.path}
            element={
              <LightModeOnly>
                <AboutPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.support.path}
            element={
              <LightModeOnly>
                <SupportPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.login.path}
            element={
              <LightModeOnly>
                <LoginPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.authCallback.path}
            element={
              <LightModeOnly>
                <AuthCallback />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.register.path}
            element={
              <LightModeOnly>
                <RegistrationPage />
              </LightModeOnly>
            }
          />
          <Route
            path={ROUTES.public.forgotPassword.path}
            element={
              <LightModeOnly>
                <ForgotPasswordPage />
              </LightModeOnly>
            }
          />

          {/* Redirect root to home */}
          <Route path="/" element={<Navigate to={ROUTES.public.home.path} replace />} />

          {/* ==================== Dashboard ==================== */}
          <Route
            path={ROUTES.dashboard.root.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.dashboard.root.requiredPermissions}
                allowedRoles={ROUTES.dashboard.root.allowedRoles}
              >
                <Layout title={ROUTES.dashboard.root.title} description={ROUTES.dashboard.root.description}>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Account & Settings ==================== */}
          <Route
            path={ROUTES.account.myAccount.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.account.myAccount.requiredPermissions}
                allowedRoles={ROUTES.account.myAccount.allowedRoles}
              >
                <Layout title={ROUTES.account.myAccount.title} description={ROUTES.account.myAccount.description}>
                  <MyAccountPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.account.settings.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.account.settings.requiredPermissions}
                allowedRoles={ROUTES.account.settings.allowedRoles}
              >
                <Layout title={ROUTES.account.settings.title} description={ROUTES.account.settings.description}>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Cloud Advisor Routes ==================== */}
          <Route
            path={ROUTES.cloudAdvisor.cost.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudAdvisor.cost.requiredPermissions}
                allowedRoles={ROUTES.cloudAdvisor.cost.allowedRoles}
              >
                <Layout title={ROUTES.cloudAdvisor.cost.title} description={ROUTES.cloudAdvisor.cost.description}>
                  <CostAdvisorPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudAdvisor.performance.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudAdvisor.performance.requiredPermissions}
                allowedRoles={ROUTES.cloudAdvisor.performance.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudAdvisor.performance.title}
                  description={ROUTES.cloudAdvisor.performance.description}
                >
                  <PerformanceAdvisorPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Cloud Manager Routes ==================== */}
          <Route
            path={ROUTES.cloudManager.costAlert.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudManager.costAlert.requiredPermissions}
                allowedRoles={ROUTES.cloudManager.costAlert.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudManager.costAlert.title}
                  description={ROUTES.cloudManager.costAlert.description}
                >
                  <CostAlertPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudManager.performanceAlert.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudManager.performanceAlert.requiredPermissions}
                allowedRoles={ROUTES.cloudManager.performanceAlert.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudManager.performanceAlert.title}
                  description={ROUTES.cloudManager.performanceAlert.description}
                >
                  <PerformanceAlertPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudManager.manageServer.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudManager.manageServer.requiredPermissions}
                allowedRoles={ROUTES.cloudManager.manageServer.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudManager.manageServer.title}
                  description={ROUTES.cloudManager.manageServer.description}
                >
                  <ManageServerPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudManager.reports.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudManager.reports.requiredPermissions}
                allowedRoles={ROUTES.cloudManager.reports.allowedRoles}
              >
                <Layout title={ROUTES.cloudManager.reports.title} description={ROUTES.cloudManager.reports.description}>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Cloud Allocation Routes ==================== */}
          <Route
            path={ROUTES.cloudAllocation.departments.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudAllocation.departments.requiredPermissions}
                allowedRoles={ROUTES.cloudAllocation.departments.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudAllocation.departments.title}
                  description={ROUTES.cloudAllocation.departments.description}
                >
                  <DepartmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudAllocation.members.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudAllocation.members.requiredPermissions}
                allowedRoles={ROUTES.cloudAllocation.members.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudAllocation.members.title}
                  description={ROUTES.cloudAllocation.members.description}
                >
                  <MembersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.cloudAllocation.unassigned.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.cloudAllocation.unassigned.requiredPermissions}
                allowedRoles={ROUTES.cloudAllocation.unassigned.allowedRoles}
              >
                <Layout
                  title={ROUTES.cloudAllocation.unassigned.title}
                  description={ROUTES.cloudAllocation.unassigned.description}
                >
                  <UnassignedPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Billing Routes ==================== */}
          <Route
            path={ROUTES.billing.alerts.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.alerts.requiredPermissions}
                allowedRoles={ROUTES.billing.alerts.allowedRoles}
              >
                <Layout title={ROUTES.billing.alerts.title} description={ROUTES.billing.alerts.description}>
                  <BillingAlertsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* <Route
            path={ROUTES.billing.awsAccounts.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.awsAccounts.requiredPermissions}
                allowedRoles={ROUTES.billing.awsAccounts.allowedRoles}
              >
                <Layout title={ROUTES.billing.awsAccounts.title} description={ROUTES.billing.awsAccounts.description}>
                  <AwsAccountsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.billing.invoices.list.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.invoices.list.requiredPermissions}
                allowedRoles={ROUTES.billing.invoices.list.allowedRoles}
              >
                <Layout
                  title={ROUTES.billing.invoices.list.title}
                  description={ROUTES.billing.invoices.list.description}
                >
                  <InvoicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.billing.payments.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.payments.requiredPermissions}
                allowedRoles={ROUTES.billing.payments.allowedRoles}
              >
                <Layout title={ROUTES.billing.payments.title} description={ROUTES.billing.payments.description}>
                  <PaymentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.billing.snapshots.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.snapshots.requiredPermissions}
                allowedRoles={ROUTES.billing.snapshots.allowedRoles}
              >
                <Layout title={ROUTES.billing.snapshots.title} description={ROUTES.billing.snapshots.description}>
                  <SnapshotsPage />
                </Layout>
              </ProtectedRoute>
            }
          /> */}

          <Route
            path={ROUTES.billing.costUsage.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.billing.costUsage.requiredPermissions}
                allowedRoles={ROUTES.billing.costUsage.allowedRoles}
              >
                <Layout title={ROUTES.billing.costUsage.title} description={ROUTES.billing.costUsage.description}>
                  <CostAndUsagePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Notifications ==================== */}
          <Route
            path={ROUTES.notifications.list.path}
            element={
              <ProtectedRoute
                requiredPermissions={ROUTES.notifications.list.requiredPermissions}
                allowedRoles={ROUTES.notifications.list.allowedRoles}
              >
                <Layout title={ROUTES.notifications.list.title} description={ROUTES.notifications.list.description}>
                  <NotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Department Admin Routes ==================== */}
          <Route
            path={ROUTES.deptAdmin.dashboard.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.dashboard.requiredPermissions} allowedRoles={ROUTES.deptAdmin.dashboard.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.dashboard.title} description={ROUTES.deptAdmin.dashboard.description}>
                  <DeptAdminDashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptAdmin.department.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.department.requiredPermissions} allowedRoles={ROUTES.deptAdmin.department.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.department.title} description={ROUTES.deptAdmin.department.description}>
                  <DeptAdminDepartmentPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptAdmin.members.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.members.requiredPermissions} allowedRoles={ROUTES.deptAdmin.members.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.members.title} description={ROUTES.deptAdmin.members.description}>
                  <DeptAdminMembersPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptAdmin.notifications.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.notifications.requiredPermissions} allowedRoles={ROUTES.deptAdmin.notifications.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.notifications.title} description={ROUTES.deptAdmin.notifications.description}>
                  <DeptAdminNotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptAdmin.account.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.account.requiredPermissions} allowedRoles={ROUTES.deptAdmin.account.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.account.title} description={ROUTES.deptAdmin.account.description}>
                  <DeptAdminAccountPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptAdmin.settings.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptAdmin.settings.requiredPermissions} allowedRoles={ROUTES.deptAdmin.settings.allowedRoles}>
                <Layout title={ROUTES.deptAdmin.settings.title} description={ROUTES.deptAdmin.settings.description}>
                  <DeptAdminSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Department Member Routes ==================== */}
          <Route
            path={ROUTES.deptMember.dashboard.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptMember.dashboard.requiredPermissions} allowedRoles={ROUTES.deptMember.dashboard.allowedRoles}>
                <Layout title={ROUTES.deptMember.dashboard.title} description={ROUTES.deptMember.dashboard.description}>
                  <DeptMemberDashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptMember.department.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptMember.department.requiredPermissions} allowedRoles={ROUTES.deptMember.department.allowedRoles}>
                <Layout title={ROUTES.deptMember.department.title} description={ROUTES.deptMember.department.description}>
                  <DeptMemberDepartmentPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptMember.notifications.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptMember.notifications.requiredPermissions} allowedRoles={ROUTES.deptMember.notifications.allowedRoles}>
                <Layout title={ROUTES.deptMember.notifications.title} description={ROUTES.deptMember.notifications.description}>
                  <DeptMemberNotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptMember.account.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptMember.account.requiredPermissions} allowedRoles={ROUTES.deptMember.account.allowedRoles}>
                <Layout title={ROUTES.deptMember.account.title} description={ROUTES.deptMember.account.description}>
                  <DeptMemberAccountPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.deptMember.settings.path}
            element={
              <ProtectedRoute requiredPermissions={ROUTES.deptMember.settings.requiredPermissions} allowedRoles={ROUTES.deptMember.settings.allowedRoles}>
                <Layout title={ROUTES.deptMember.settings.title} description={ROUTES.deptMember.settings.description}>
                  <DeptMemberSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* ==================== Legacy redirect-only paths ==================== */}
          <Route
            path={ROUTES.legacy.billingRoot.path}
            element={
              <ProtectedRoute>
                <RoleAwareRedirect />
              </ProtectedRoute>
            }
          />

          {/* ==================== 404 Route ==================== */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  )
}

export default App
